import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  LayoutDashboard, FileText, AlertTriangle, Construction, CheckCircle, Clock,
  Eye, MapPin, Download, Users, TrendingUp, Loader2
} from 'lucide-react';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  verified: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  in_progress: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  resolved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

export default function AdminDashboardPage() {
  const { authAxios, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedReport, setSelectedReport] = useState(null);
  const [updating, setUpdating] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    fetchData();
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [reportsRes, statsRes] = await Promise.all([
        authAxios.get('/admin/reports?limit=200'),
        authAxios.get('/admin/stats')
      ]);
      setReports(reportsRes.data.reports || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const updateStatus = async (reportId, newStatus) => {
    setUpdating(reportId);
    try {
      await authAxios.patch(`/reports/${reportId}/status`, { status: newStatus });
      setReports(prev => prev.map(r => r.id === reportId ? { ...r, status: newStatus } : r));
      if (selectedReport?.id === reportId) setSelectedReport(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      console.error(err);
    }
    setUpdating('');
  };

  const filtered = reports.filter(r => {
    if (typeFilter !== 'all' && r.report_type !== typeFilter) return false;
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    return true;
  });

  const downloadCSV = () => {
    const headers = ['ID', 'Type', 'Category', 'Status', 'Latitude', 'Longitude', 'Vehicle', 'Date', 'Reporter'];
    const rows = filtered.map(r => [
      r.id, r.report_type, r.category, r.status,
      r.latitude, r.longitude, r.vehicle_number || '',
      r.created_at, r.user_name || 'Anonymous'
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'reports.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  if (!user || user.role !== 'admin') return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  const statCards = [
    { label: 'Total Reports', value: stats?.total_reports || 0, icon: FileText, color: 'text-primary' },
    { label: 'Violations', value: stats?.violations || 0, icon: AlertTriangle, color: 'text-accent' },
    { label: 'Blackspots', value: stats?.blackspots || 0, icon: Construction, color: 'text-[hsl(var(--warning))]' },
    { label: 'Resolved', value: stats?.resolved || 0, icon: CheckCircle, color: 'text-[hsl(var(--success))]' },
    { label: 'Pending', value: stats?.pending || 0, icon: Clock, color: 'text-muted-foreground' },
    { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'text-[hsl(var(--info))]' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="admin-dashboard-page">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <LayoutDashboard className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Admin Dashboard</h1>
          <p className="text-sm text-muted-foreground">Traffic Police Command Center</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="admin-stats-grid">
        {statCards.map(s => (
          <Card key={s.label}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <s.icon className={`w-4 h-4 ${s.color}`} />
              </div>
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters & Actions */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-36" data-testid="admin-type-filter">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="violation">Violations</SelectItem>
            <SelectItem value="blackspot">Blackspots</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36" data-testid="admin-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="outline" className="text-xs">{filtered.length} reports</Badge>
        <Button variant="outline" size="sm" className="ml-auto gap-2" onClick={downloadCSV} data-testid="admin-download-csv">
          <Download className="w-4 h-4" />Export CSV
        </Button>
      </div>

      {/* Reports Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Category</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Reporter</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No reports found</TableCell></TableRow>
                ) : (
                  filtered.map(report => (
                    <TableRow key={report.id} data-testid={`admin-report-row-${report.id}`}>
                      <TableCell className="font-medium text-sm">{report.category}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs capitalize">{report.report_type}</Badge>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusColors[report.status]}`}>
                          {report.status?.replace('_', ' ')}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs font-mono">
                        {report.latitude?.toFixed(4)}, {report.longitude?.toFixed(4)}
                      </TableCell>
                      <TableCell className="text-sm">{report.user_name || 'Anonymous'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(report.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="sm" onClick={() => setSelectedReport(report)} data-testid={`view-report-${report.id}`}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                  {report.report_type === 'violation' ? <AlertTriangle className="w-5 h-5 text-accent" /> : <Construction className="w-5 h-5 text-[hsl(var(--warning))]" />}
                                  {report.category}
                                </DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4 mt-4">
                                {report.media_url && (
                                  <img
                                    src={`${process.env.REACT_APP_BACKEND_URL}${report.media_url}`}
                                    alt="Evidence"
                                    className="w-full max-h-64 object-cover rounded-lg"
                                  />
                                )}
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                  <div className="p-3 rounded-lg bg-muted">
                                    <span className="text-xs text-muted-foreground block">Type</span>
                                    <span className="font-medium capitalize">{report.report_type}</span>
                                  </div>
                                  <div className="p-3 rounded-lg bg-muted">
                                    <span className="text-xs text-muted-foreground block">Status</span>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusColors[report.status]}`}>
                                      {report.status?.replace('_', ' ')}
                                    </span>
                                  </div>
                                  <div className="p-3 rounded-lg bg-muted">
                                    <span className="text-xs text-muted-foreground block">Latitude</span>
                                    <span className="font-mono">{report.latitude?.toFixed(6)}</span>
                                  </div>
                                  <div className="p-3 rounded-lg bg-muted">
                                    <span className="text-xs text-muted-foreground block">Longitude</span>
                                    <span className="font-mono">{report.longitude?.toFixed(6)}</span>
                                  </div>
                                  {report.vehicle_number && (
                                    <div className="p-3 rounded-lg bg-muted">
                                      <span className="text-xs text-muted-foreground block">Vehicle</span>
                                      <span className="font-mono">{report.vehicle_number}</span>
                                    </div>
                                  )}
                                  <div className="p-3 rounded-lg bg-muted">
                                    <span className="text-xs text-muted-foreground block">Reporter</span>
                                    <span>{report.user_name || 'Anonymous'}</span>
                                  </div>
                                  <div className="p-3 rounded-lg bg-muted">
                                    <span className="text-xs text-muted-foreground block">Device</span>
                                    <span>{report.device_type || 'Unknown'}</span>
                                  </div>
                                  <div className="p-3 rounded-lg bg-muted">
                                    <span className="text-xs text-muted-foreground block">GPS Accuracy</span>
                                    <span className="font-mono">{report.gps_accuracy ? `${report.gps_accuracy.toFixed(1)}m` : 'N/A'}</span>
                                  </div>
                                  <div className="p-3 rounded-lg bg-muted">
                                    <span className="text-xs text-muted-foreground block">IP Address</span>
                                    <span className="font-mono text-xs">{report.ip_address || 'N/A'}</span>
                                  </div>
                                  <div className="p-3 rounded-lg bg-muted">
                                    <span className="text-xs text-muted-foreground block">File Hash</span>
                                    <span className="font-mono text-xs truncate block">{report.file_hash || 'N/A'}</span>
                                  </div>
                                </div>
                                {report.description && (
                                  <div className="p-3 rounded-lg bg-muted">
                                    <span className="text-xs text-muted-foreground block mb-1">Description</span>
                                    <p className="text-sm">{report.description}</p>
                                  </div>
                                )}
                                {/* Status Update */}
                                <div className="flex flex-wrap gap-2 pt-2 border-t">
                                  <span className="text-sm font-medium self-center mr-2">Update Status:</span>
                                  {['verified', 'in_progress', 'resolved', 'rejected'].map(s => (
                                    <Button
                                      key={s}
                                      variant="outline"
                                      size="sm"
                                      disabled={report.status === s || updating === report.id}
                                      onClick={() => updateStatus(report.id, s)}
                                      className="capitalize text-xs"
                                      data-testid={`set-status-${s}-${report.id}`}
                                    >
                                      {updating === report.id ? <Loader2 className="w-3 h-3 animate-spin" /> : s.replace('_', ' ')}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                          <Select
                            value={report.status}
                            onValueChange={(val) => updateStatus(report.id, val)}
                          >
                            <SelectTrigger className="w-28 h-8 text-xs" data-testid={`status-select-${report.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="verified">Verified</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="resolved">Resolved</SelectItem>
                              <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
